import axiosInstance from '@/setup/axios/axiosInstance'
import { TimekeepingModel } from '../models/TimekeepingModel'
import { TimesheetModel } from '../models/TimesheetModel'
import { AxiosResponse } from 'axios'

// Reuse in-flight requests by URL to avoid duplicate calls from overlapping effects.
const inFlightRequests = new Map<string, Promise<AxiosResponse<any>>>()
const responseCache = new Map<string, { expireAt: number; response: AxiosResponse<any> }>()
const TIMEKEEPINGS_BASE_PATH = '/timekeepings'
const TIMESHEETS_BASE_PATH = '/timesheets'

const getCachedResponse = <T>(url: string): AxiosResponse<T> | null => {
  const cached = responseCache.get(url)
  if (!cached) return null

  if (Date.now() > cached.expireAt) {
    responseCache.delete(url)
    return null
  }

  return cached.response as AxiosResponse<T>
}

const setCachedResponse = <T>(url: string, response: AxiosResponse<T>, ttlMs: number) => {
  if (ttlMs <= 0) return
  responseCache.set(url, {
    expireAt: Date.now() + ttlMs,
    response: response as AxiosResponse<any>,
  })
}

const getOrCreateRequest = <T>(url: string): Promise<AxiosResponse<T>> => {
  const existing = inFlightRequests.get(url) as Promise<AxiosResponse<T>> | undefined;
  if (existing) {
    return existing;
  }

  const request = axiosInstance.get<T>(url)
    .finally(() => {
      inFlightRequests.delete(url)
    })

  inFlightRequests.set(url, request as Promise<AxiosResponse<any>>)
  return request
}

const getOrCreateRequestWithCache = <T>(url: string, ttlMs: number): Promise<AxiosResponse<T>> => {
  const cached = getCachedResponse<T>(url)
  if (cached) {
    return Promise.resolve(cached)
  }

  const existing = inFlightRequests.get(url) as Promise<AxiosResponse<T>> | undefined
  if (existing) {
    return existing
  }

  const request = axiosInstance.get<T>(url)
    .then((response) => {
      setCachedResponse(url, response, ttlMs)
      return response
    })
    .finally(() => {
      inFlightRequests.delete(url)
    })

  inFlightRequests.set(url, request as Promise<AxiosResponse<any>>)
  return request
}

// Get all timekeepings
export interface TimekeepingFilters {
  search?: string;
  employee?: string;
  date?: string;
  date__gte?: string;
  date__lte?: string;
  worked_morning?: boolean;
  worked_afternoon?: boolean;
  workday_count?: number;
  workday_count__gte?: number;
  workday_count__lte?: number;
  shift_code?: string;
  order?: string;
  no_pagination?: boolean;
  page?: number;
  page_size?: number;
  company?: string;
}

export interface TimesheetFilters {
  month?: number;
  year?: number;
  salary_month?: number;
  salary_year?: number;
  period?: string;
  company?: string;
  employee_ids?: string[];
  action?: string;
  no_pagination?: boolean;
  timesheet?: boolean;
}

export interface CreateTimesheetRequest {
  month: number;
  year: number;
  company_id?: string;
  employee_id?: string;
  employee_ids?: string[];
}

export type UpdateTimesheetRequest = Partial<TimesheetModel> & {
  action?: string;
  month?: number;
  year?: number;
  company_id?: string;
}

export function getTimekeepings(filters?: TimekeepingFilters) {
  const params = new URLSearchParams();
  if (filters?.search && filters.search.trim()) {
    params.append('search', filters.search.trim());
  }
  if (filters?.employee) {
    params.append('employee', filters.employee);
  }
  if (filters?.date) {
    params.append('date', filters.date);
  }
  if (filters?.date__gte) {
    params.append('date__gte', filters.date__gte);
  }
  if (filters?.date__lte) {
    params.append('date__lte', filters.date__lte);
  }
  if (filters?.worked_morning !== undefined) {
    params.append('worked_morning', filters.worked_morning.toString());
  }
  if (filters?.worked_afternoon !== undefined) {
    params.append('worked_afternoon', filters.worked_afternoon.toString());
  }
  if (filters?.workday_count !== undefined) {
    params.append('workday_count', filters.workday_count.toString());
  }
  if (filters?.workday_count__gte !== undefined) {
    params.append('workday_count__gte', filters.workday_count__gte.toString());
  }
  if (filters?.workday_count__lte !== undefined) {
    params.append('workday_count__lte', filters.workday_count__lte.toString());
  }
  if (filters?.shift_code) {
    params.append('shift_code', filters.shift_code);
  }
  if (filters?.order) {
    params.append('order', filters.order);
  }
  if (filters?.no_pagination) {
    params.append('no_pagination', 'true');
  }
  if (filters?.page !== undefined) {
    params.append('page', String(filters.page));
  }
  if (filters?.page_size !== undefined) {
    params.append('page_size', String(filters.page_size));
  }
  if (filters?.company) {
    params.append('company', filters.company);
  }
  const queryString = params.toString();
  const url = `${TIMEKEEPINGS_BASE_PATH}/${queryString ? `?${queryString}` : ''}`;
  return getOrCreateRequest<TimekeepingModel[]>(url)
}

export function getTimesheets(filters?: TimesheetFilters) {
  const params = new URLSearchParams()

  if (filters?.month !== undefined) {
    params.append('month', String(filters.month))
  }
  if (filters?.year !== undefined) {
    params.append('year', String(filters.year))
  }
  if (filters?.salary_month !== undefined) {
    params.append('salary_month', String(filters.salary_month))
  }
  if (filters?.salary_year !== undefined) {
    params.append('salary_year', String(filters.salary_year))
  }
  if (filters?.period) {
    params.append('period', filters.period)
  }
  if (filters?.company) {
    params.append('company', filters.company)
  }
  if (filters?.employee_ids && filters.employee_ids.length > 0) {
    params.append('employee_ids', filters.employee_ids.join(','))
  }
  if (filters?.action) {
    params.append('action', filters.action)
  }

  // Timesheet list should return full data without paging.
  params.append('no_pagination', String(filters?.no_pagination ?? true))
  params.append('timesheet', String(filters?.timesheet ?? true))

  const queryString = params.toString()
  const url = `${TIMESHEETS_BASE_PATH}/${queryString ? `?${queryString}` : ''}`
  return getOrCreateRequest<TimesheetModel[] | { results?: TimesheetModel[] }>(url)
}

export function createTimesheet(payload: CreateTimesheetRequest) {
  return axiosInstance.post(`${TIMESHEETS_BASE_PATH}/`, payload)
}

export function updateTimesheet(id: string, payload: UpdateTimesheetRequest) {
  return axiosInstance.patch<TimesheetModel>(`${TIMESHEETS_BASE_PATH}/${id}/`, payload)
}

export function recalculateTimesheet(id: string, params: Record<string, unknown>) {
  return axiosInstance.get<TimesheetModel>(`${TIMESHEETS_BASE_PATH}/${id}/`, { params })
}

export function bulkUpdateTimesheetStatus(ids: (string | number)[], status: string) {
  return axiosInstance.post(`${TIMESHEETS_BASE_PATH}/bulk_update/`, { ids, status })
}

export function bulkFetchTimesheetsByIds(ids: (string | number)[]) {
  return axiosInstance.post<TimesheetModel[] | { results?: TimesheetModel[] }>(`${TIMESHEETS_BASE_PATH}/bulk_fetch/`, {ids})
}

export function bulkFetchRevenueByIds(ids: (string | number)[]) {
  return axiosInstance.post<TimesheetModel[] | { results?: TimesheetModel[] }>(`${TIMESHEETS_BASE_PATH}/bulk_revenue_print/`, {ids})
}

export interface BulkRevenueRequest {
  month: number;
  year: number;
  company: string;
  employee_ids: string[];
  type: 'sale' | 'survey' | 'cnc';
}

export interface BulkRevenueItem {
  employee_id: string;
  order_code: string;
  customer_name: string;
  total_price: number;
  total_price_not_tax: number;
  sales_employee_name: string;
}

export type BulkRevenueResponse = BulkRevenueItem[];

export function getBulkRevenue(payload: BulkRevenueRequest) {
  return axiosInstance.post<BulkRevenueResponse>('/orders/get_bulk_revenue/', payload);
}

export function bulkRefreshTimesheets(payload: { timesheet_ids: (string | number)[] }) {
  return axiosInstance.post(`${TIMESHEETS_BASE_PATH}/bulk_refresh/`, payload);
}

export function deleteTimesheet(id: number | string) {
  return axiosInstance.delete(`${TIMESHEETS_BASE_PATH}/${id}/`)
}

// Get single timekeeping by ID
export function getTimekeeping(id: string) {
  return axiosInstance.get<TimekeepingModel>(`${TIMEKEEPINGS_BASE_PATH}/${id}/`)
}

// Create new timekeeping
export function createTimekeeping(timekeepingData: Partial<TimekeepingModel>) {
  return axiosInstance.post<TimekeepingModel>(`${TIMEKEEPINGS_BASE_PATH}/`, timekeepingData)
}

export interface BulkTimekeepingQuery {
  status?: string;
  attendance_type?: string;
  company?: string;
}

export function bulkUpsertTimekeepings(payload: Record<string, any>, query?: BulkTimekeepingQuery) {
  const params = new URLSearchParams();
  if (query?.status) {
    params.append('status', query.status);
  }
  if (query?.attendance_type) {
    params.append('attendance_type', query.attendance_type);
  }
  if (query?.company) {
    params.append('company', query.company);
  }

  const queryString = params.toString();
  const url = `${TIMEKEEPINGS_BASE_PATH}/bulk/${queryString ? `?${queryString}` : ''}`;
  return axiosInstance.post(url, payload);
}

// Bulk create/update timekeepings — single payload with employee_ids array + common fields
export function bulkCreateTimekeepings(payload: Record<string, any>) {
  const url = `${TIMEKEEPINGS_BASE_PATH}/bulk/`;
  return axiosInstance.post(url, payload);
}

// Update timekeeping
export function updateTimekeeping(id: string, timekeepingData: Partial<TimekeepingModel>) {
  return axiosInstance.put<TimekeepingModel>(`${TIMEKEEPINGS_BASE_PATH}/${id}/`, timekeepingData)
}

// Delete timekeeping (soft delete)
export function deleteTimekeeping(id: string) {
  return axiosInstance.delete(`${TIMEKEEPINGS_BASE_PATH}/${id}/`)
}

// Soft delete multiple timekeepings
export function softDeleteTimekeepings(timekeepingIds: string[]) {
  return axiosInstance.post(`${TIMEKEEPINGS_BASE_PATH}/soft_delete/`, { timekeeping_ids: timekeepingIds })
}

// Restore deleted timekeepings
export function restoreTimekeepings(timekeepingIds: string[]) {
  return axiosInstance.post(`${TIMEKEEPINGS_BASE_PATH}/restore_deleted/`, { timekeeping_ids: timekeepingIds })
}

// Permanently delete timekeepings
export function permanentDeleteTimekeepings(timekeepingIds: string[]) {
  return axiosInstance.post(`${TIMEKEEPINGS_BASE_PATH}/permanent_delete/`, { timekeeping_ids: timekeepingIds })
}

// Get dates summary (list of dates with timekeeping data)
export interface DateSummaryItem {
  date: string;
  total_employees: number;
  total_workday: number;
}

export function getDatesSummary(month?: string, company?: string) {
  const params = new URLSearchParams();
  if (month) {
    params.append('month', month);
  }
  if (company) {
    params.append('company', company);
  }
  const queryString = params.toString();
  const url = `${TIMEKEEPINGS_BASE_PATH}/dates_summary/${queryString ? `?${queryString}` : ''}`;
  return getOrCreateRequest<DateSummaryItem[]>(url)
}

// Get nearest date with timekeeping data
export interface NearestDateResponse {
  date: string | null;
  message?: string;
}

export function getNearestDate() {
  return getOrCreateRequestWithCache<NearestDateResponse>(`${TIMEKEEPINGS_BASE_PATH}/nearest_date/`, 5000)
}

// Site Allowance Configuration
export interface SiteAllowanceConfig {
  id: string;
  department_ids: string[];
  default_amount: number | Record<string, number>;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
  updated_by?: string | null;
}

// Get site allowance configuration
export function getSiteAllowanceConfig() {
  return getOrCreateRequestWithCache<SiteAllowanceConfig>(`${TIMEKEEPINGS_BASE_PATH}/site-allowance-config/`, 5000)
}

// Update site allowance configuration
export function updateSiteAllowanceConfig(data: {
  department_ids: string[];
  default_amount?: number | Record<string, number>;
}) {
  return axiosInstance.put<SiteAllowanceConfig>(`${TIMEKEEPINGS_BASE_PATH}/site-allowance-config/`, data)
}



